/* eslint-disable max-params */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { type FragmentData, type TranslateFragmentInTask } from '../translate-fragment-in-task';
import { TranslateLoadingService } from '../translate-loading-service';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { FactoryPsHttpSSE, PsHttpSSE } from '@/services/sse-ps-http/sse-ps-http';
import type { TokenUsage } from '@/infrastructure/api-functions/core-drafting-api/types';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';
import { getCurrentLanguage } from '@telecom/shared/utils/language-tool';
import { create, Emitter } from '@patsnap/tahiti-core';
import { SseState } from '../../types';
import { TranslateModel } from '../../models/translate-model';
import type { StreamStatus } from '../../enums';

interface RegenerateFragmentInTranslationResponse {
    content: string;
    token_usage: TokenUsage;
    status: StreamStatus;
}

@injectable()
export class TranslateFragmentInTaskImpl implements TranslateFragmentInTask {
    constructor(
        @inject(TranslateLoadingService)
        private readonly _translateLoadingService: TranslateLoadingService,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(TranslateModel)
        private readonly _aiTranslationModel: TranslateModel,
        @inject(FactoryPsHttpSSE)
        private _factorySsePsHttp: FactoryPsHttpSSE,
    ) {}

    private _stateChangeEmitter = new Emitter<SseState>();

    private _fireStateChange(state: SseState) {
        this._stateChangeEmitter.fire(state);
    }

    get onStateChange() {
        return this._stateChangeEmitter.event;
    }

    /**
     * 最后一次传递的 fragment
     */
    private _lastFragment: FragmentData | null = null;

    async translateFragment(fragment: FragmentData): Promise<void> {
        this._lastFragment = fragment;
        this._translateLoadingService.setLoadingRegenerateTranslateFragment(true);
        this._translateLoadingService.setCurrentTranslateFragmentIndex(fragment.index);
        this._updateTranslationResultToModel(fragment, '');
        try {
            await this._sendToServer(fragment);
        }
        finally {
            this._translateLoadingService.setLoadingRegenerateTranslateFragment(false);
        }
    }

    regenerateTranslation(fragment: { index: number; }): Promise<void> {
        const item = this._aiTranslationModel.modelData.result[fragment.index];
        if (!item) {
            throw new Error('item is not found');
        }
        const text = item.original;
        return this.translateFragment({ index: fragment.index, original: text });
    }

    /**
     * 更新翻译结果到模型中
     * @param fragment 原文片段
     * @param result 译文结果
     * @returns 还原翻译结果到模型中的函数
     */
    private _updateTranslationResultToModel(fragment: FragmentData, result: string) {
        this._aiTranslationModel.update(state => {
            state.result[fragment.index] = {
                original: fragment.original,
                translation: result,
                graphIndex: 0,
            };
            return state;
        });
    }

    private _sse: PsHttpSSE<RegenerateFragmentInTranslationResponse> | null = null;

    private async _sendToServer(fragment: FragmentData): Promise<void> {
        const taskId = this._currentTaskService.taskId;
        const deferred = create<void>();
        this._sse = this._getTranslateFragmentFromServer(taskId, fragment)
            .onOpen(() => {
                this._fireStateChange(SseState.WaitingFirstOutput);
            })
            .onMessage(data => {
                const result = data.data.content;
                this._updateTranslationResultToModel(fragment, result);
                this._fireStateChange(SseState.Outputting);
            })
            .onError(err => {
                this._fireStateChange(SseState.OutputFailed);
                deferred.reject(err);
            })
            .onFinish(() => {
                this._fireStateChange(SseState.OutputCompleted);
                deferred.resolve();
            });
        this._sse.start();
        return deferred.promise;
    }

    abort() {
        if (this._sse) {
            this._sse.close();
        }
    }

    retry() {
        if (!this._lastFragment) {
            return;
        }
        this.translateFragment(this._lastFragment);
    }

    private _getTranslateFragmentFromServer(taskId: string, fragment: FragmentData) {
        const sse = this._factorySsePsHttp<RegenerateFragmentInTranslationResponse>().init({
            method: 'PUT',
            url: `${coreTelecomApiUrlPrefix}/ai-translation/original`,
            data: {
                task_id: taskId,
                original_index: fragment.index,
                original_text: fragment.original,
            },
            headers: {
                lang: getCurrentLanguage(),
                from: 's-core-analytics',
            },
        });
        return sse;
    }
}
